import S from 's-js';
import { CachedModelLoader } from 'lib/CachedModelLoader';
import TZDate from 'lib/TZDate';
import { TZUtils } from 'lib/tzutils';

import { CoordinationCard } from 'data/models/CoordinationCard';
import { HomePageDateMovements, HomePageDateMovementsOptions, HomePageMovement, HomePagePortMovements } from 'data/models/HomePage';
import { UserSummary } from 'data/models/UserSummary';

import { PCCache } from 'data/PCCache';

import { PortCallSchedule, PortCallScheduleParams } from 'portcall/portcallschedule/model';

import { PortFilterer } from './model';

export type MovementsLoader = CachedModelLoader<HomePageDateMovements, HomePageDateMovementsOptions>;

export const Movements = (
    loader : MovementsLoader,
    data : PCCache,
    date : () => TZDate,
    user : () => UserSummary | null,
    portFilterer : PortFilterer,
    shown : () => boolean,
    tzutils : TZUtils,
    portcallScheduleModel : (params : PortCallScheduleParams) => PortCallSchedule
) => {
        var dateData = S(() => shown() ? loader({ Date: date().toJSON() }) : null),
            Loading = () => dateData() !== null && dateData()!.loading(),
            Ports = () => dateData() && dateData()!.model.Ports().map(PortMovementsModel) || [],
            DisplayedPorts = portFilterer(Ports, m => m.PortName()!),
            SelectedMovement = S.value<HomePageMovement | null>(null),
            MovementDetails = S(() => SelectedMovement() && MovementDetailsModel(SelectedMovement()!));
        
        return { Date : date, TZUtils: tzutils, Loading, DisplayedPorts, MovementDetails };
        
        function PortMovementsModel(port : HomePagePortMovements) {
            return {
                PortId: port.PortId,
                PortName: port.PortName,
                Movements: port.Movements.map(PortMovementModel).orderBy(m => +(m.MovementStartTime() || 0))
            };
        }
        
        function PortMovementModel(m : HomePageMovement) {
            const
                // movement has occured if it has either departure or arrival actual time  
                HasBegun = () => m.FromPoint() && m.FromPoint()!.Departure.Actual() !== null
                    || m.ToPoint() && m.ToPoint()!.Arrival.Actual() !== null
                    || false,
                MovementStartTime = () => 
                    m.FromPoint() === null ? null :
                    m.FromPoint()!.Departure.Effective(),
                MovementCompleteTime = () =>
                    m.ToPoint() === null ? null :
                    m.ToPoint()!.Arrival.Actual(),
                TrafficCoordinationCards = m.TrafficCoordinationCards
                    .map(ccard => TrafficCoordinationCardModel(ccard))
                    .sort((ccard1, ccard2) =>
                        // C-Cards for arrival point first, then departure point, in usual C-Card display order
                        // within each of those groups.
                        ccard1.Model.ScheduleDateId() === ccard2.Model.ScheduleDateId() ?
                            // Both C-Cards are for the same milestone date. Sort in the usual C-Card order.
                            CoordinationCard.DisplayOrder(ccard1.Model, ccard2.Model) :
                            ccard1.Model.ScheduleDate()?.ItineraryPointId() === m.FromPointId() ?
                                // ccard1 is for the from point. Assume that means ccard2 is for the to point
                                // (the server should only be sending us C-Cards for at most one milestone date
                                // per itinerary point per movement) and sort ccard1 before ccard2.
                                -1 :
                                // Otherwise, assume ccard1 is for the to point and ccard2 is for the from point.
                                1
                    );

            return {
                Model: m,
                HasBegun,
                MovementStartTime,
                MovementCompleteTime,
                TrafficCoordinationCards,
                ShowDetails,
            };

            function ShowDetails() {
                if (S.sample(user)) SelectedMovement(m);
            }
        }

        function TrafficCoordinationCardModel(ccard : CoordinationCard) {
            // Nothing for now (just future-proofing in case we find a reason to add extra properties/state later).
            return { Model: ccard };
        }

        function MovementDetailsModel(m : HomePageMovement) {
            const
                // The server doesn't actually include the PortCall entity w/ its Movement object graph. So
                // upsert an empty PortCall object ourselves, to be filled in more by the PortCallSchedule
                // component when it loads its data.
                PortCall = data.PortCalls.update1(null, { Id: m.PortCallId() }),
                Schedule = portcallScheduleModel({ PortCall }),
                ShowActionsMenu = S.value(false);

            return {
                Movement: m,
                Schedule,
                ShowActionsMenu,
                ToggleActionsMenu,
                Close,
            };

            function ToggleActionsMenu() {
                ShowActionsMenu(!S.sample(ShowActionsMenu));
            }

            function Close() {
                if (S.sample(SelectedMovement) === m) SelectedMovement(null);
            }
        }
    };

export type Movements = ReturnType<typeof Movements>;
export type PortMovements = ReturnType<Movements["DisplayedPorts"]>[0];
export type Movement = ReturnType<PortMovements["Movements"]>[0];
export type TrafficCoordinationCard = ReturnType<Movement["TrafficCoordinationCards"]>[0];
export type MovementDetails = ReturnType<Movements["MovementDetails"]> & {};



// WEBPACK FOOTER //
// ./apps/portcall/homepage/movements.ts